/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, Fragment, useMemo, useCallback, useEffect } from "react";

// antd:
import { Collapse } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
import SpanE from "../react/SpanE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getTheme, getLng, getDict, getUser, getAppState, changeAppState, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

type Taccordion = {
  accordion:boolean,
  size:"middle" | "large" | "small",
  bordered?:boolean
}

const RZaccordions:FC<TuniversalElementProps> = ({attributes}) => { // children
  RZaccordions.displayName = "Accordions";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type, content} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath];
  const {color, backgroundColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  // if (rootNamespace && nextEl.attributes.onChange && rootNamespace[nextEl.attributes.onChange]) { fltSt.onChange = rootNamespace[nextEl.attributes.onChange] }

  const accType = useMemo<Taccordion>(() => {
    const typ:Taccordion = {accordion: true, size: "middle", bordered: false};
    (type || "").replace((/(\s+|,|\|)/igm), ";").split(";").forEach((tt) => {
      const token = tt.toLowerCase();
      if (~["bordered", "borderred"].indexOf(token)) { typ.bordered = true }
      else if (~["middle", "large", "small", "middlesize", "largesize", "smallsize"].indexOf(token)) { typ.size = token.replace("size", "") as Taccordion["size"] }
      if (~["expandone"].indexOf(token)) { typ.accordion = true }
      if (~["expandmany"].indexOf(token)) { typ.accordion = false }
    });

    if (attributes?.bordered) { typ.bordered = (attributes.bordered === "true") }
    if (attributes?.accordion) { typ.accordion = (attributes.accordion === "true") }
    if (attributes?.size) { typ.size = attributes.size }
    return typ;
  }, [attributes, type]);

  const activeAccordionChange = useCallback((activeAccKey) => {
    if (fnc.config?.isDev) console.log({action: "<Accordions key='" + key + "'> -> activeAccordionChange()", activeAccKey});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, value: activeAccKey}}));
    if (rootNamespace && (attributes.onChange || attributes.onchange) && rootNamespace[attributes.onChange || attributes.onchange]) {
      const clientFNC = rootNamespace[attributes.onChange || attributes.onchange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, activeAccKey, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, activeAccKey, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, safe]);

  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng, rlng]);

  return (
    <Collapse
      key={key} className={clsu.join(" ")} uk-tooltip={tooltip} style={styl}
      activeKey={elState?.value || attributes?.value || content?.[0]?.key || "1"}
      size={accType.size}
      bordered={accType.bordered}
      accordion={accType.accordion}
      onChange={activeAccordionChange}
      items={
        content.map((tt) => {
          const perms = fnc.checkPermissions(user, {...tt?.attributes, rootAttributes: {key: rootAttributes.key}});
          const accState = appState[tt.fullPath];
          const accStyle = (accState.attributes?.style ? rz.parseCSSText(accState.attributes?.style) : {});

          // Improved
          if ((accState?.visible === false || perms.visible === false) && accStyle.display !== "none") { accStyle.display = "none" }
          else if ((accState?.visible === true && perms.visible !== false) && accStyle.display === "none") { accStyle.display = undefined }

          if (accStyle.display === "none") { return null }

          return {
            key: tt.key,
            className: accState.attributes?.class, style: accStyle,
            // className: (accState?.userClass || []).join(" "), style: tt.style,
            label: (
              <SpanE
                key={tt.key + "-span"}
                uk-tooltip={accState.attributes["tooltip_" + lng] || accState.attributes.tooltip || accState.attributes["tooltip_" + rlng]}
              >
                {accState.attributes?.icon
                  ? (<AppIcons key={tt.key + "-ico"} icopath={accState.attributes.icon}
                  attrs={{
                    size: (accState.attributes?.iconSize ? accState.attributes.iconSize : undefined),
                    color: accState.attributes?.iconColor || "black|white",
                    className: accState.attributes?.iconClass || "rz-margin-right-10"
                  }} />)
                  : undefined
                }
                {accState.attributes?.img
                  ? (<img key={tt.key + "-img"} src={accState.attributes.img}
                      style={accState.attributes.imgStyle ? rz.parseCSSText(accState.attributes?.imgStyle) : undefined}
                      className={accState.attributes.imgClass ? accState.attributes?.imgClass : undefined}
                    />)
                  : undefined
                }
                {accState.attributes["title_" + lng] || accState.attributes.title || accState.attributes["title_" + rlng] || tt.attributes["title_" + lng] || tt.attributes.title || tt.attributes["title_" + rlng]}
              </SpanE>
            ),
            disabled: (accState?.disabled || perms.disabled === true),
            hidden: (accState?.visible === false || perms.visible === false),
            children: (<Fragment key={tt.key + "-fragment"}>{(tt.content || []).map((el) => (el.children))}</Fragment>)
          }
        }).filter((tt) => (tt))
      }
    />
  );
}

export default RZaccordions;
/* eslint-enable @typescript-eslint/no-unused-vars */
